import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  evaluateBinding,
  loadUiComponentRegistry,
  MdrRenderer,
  type UiComponentRegistry,
} from '../../mdr-react/src/index.js';
import { generatedUiComponentLoaders } from '../../mdr-catalog-generated/src/component-loaders.js';
import {
  JsonValueSchema,
  type InternalNavigationTarget,
  type JsonValue,
  type UiDocumentV1,
} from '../../mdr-ui-contracts/src/index.js';
import { operationRequest, type SurfaceOperationRoute } from './operation-request.js';
import { OpsProductFrame } from './ops-product-frame.js';
import './styles.css';

export type MdrSurface = 'public' | 'ops' | 'marketing';

export interface SurfaceManifest {
  readonly surface: MdrSurface;
  readonly venueId: string;
  readonly designSystemId: string;
  readonly screens: readonly UiDocumentV1[];
  readonly copy: Readonly<Record<string, string>>;
  readonly data: Readonly<Record<string, JsonValue>>;
  readonly operationRoutes: Readonly<Record<string, SurfaceOperationRoute>>;
}

export interface GenericMdrShellProps {
  readonly surface: MdrSurface;
  readonly allowSurfaceQueryOverride?: boolean;
  readonly localBearerMode?: boolean;
  readonly apiBaseUrl?: string | undefined;
  readonly fixtureLoader?: (surface: MdrSurface) => Promise<SurfaceManifest>;
}

function requestedSurface(fallback: MdrSurface, allowOverride: boolean): MdrSurface {
  if (!allowOverride) return fallback;
  const requested = new URL(window.location.href).searchParams.get('surface');
  return requested === 'public' || requested === 'ops' || requested === 'marketing'
    ? requested
    : fallback;
}

function manifestPath(surface: MdrSurface): string {
  if (surface === 'ops') return '/api/staff/experience-manifest';
  return `/api/${surface}/experience-manifest`;
}

function apiRequestUrl(apiBaseUrl: string, path: string): string {
  if (!path.startsWith('/api/')) throw new Error(`MDR API path must start with /api/: ${path}`);
  return `${apiBaseUrl}${path}`;
}

function isJsonRecord(value: unknown): value is { readonly [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function bindData(
  source: Readonly<Record<string, JsonValue>>,
  fieldPath: string,
  value: JsonValue,
): Readonly<Record<string, JsonValue>> {
  const output: Record<string, JsonValue> = { ...source };
  const segments = fieldPath.split('.');
  let cursor = output;
  for (const [index, segment] of segments.entries()) {
    if (index === segments.length - 1) {
      cursor[segment] = value;
      break;
    }
    const current = cursor[segment];
    const next: Record<string, JsonValue> = isJsonRecord(current) ? { ...current } : {};
    cursor[segment] = next;
    cursor = next;
  }
  return output;
}

function defaultScreen(screens: readonly UiDocumentV1[]): UiDocumentV1 | undefined {
  if (!screens.some(({ navigation }) => navigation !== undefined)) {
    return screens[0];
  }
  return [...screens].sort((left, right) => {
    const leftNavigation = left.navigation;
    const rightNavigation = right.navigation;
    return (
      (leftNavigation?.groupOrder ?? 1_000) - (rightNavigation?.groupOrder ?? 1_000) ||
      (leftNavigation?.order ?? 1_000) - (rightNavigation?.order ?? 1_000) ||
      left.screenId.localeCompare(right.screenId)
    );
  })[0];
}

export function GenericMdrShell({
  surface: configuredSurface,
  allowSurfaceQueryOverride = false,
  localBearerMode = false,
  apiBaseUrl: configuredApiBaseUrl = '',
  fixtureLoader,
}: GenericMdrShellProps) {
  const apiBaseUrl = configuredApiBaseUrl.replace(/\/+$/, '');
  const [surface, setSurface] = useState<MdrSurface>(() =>
    requestedSurface(configuredSurface, allowSurfaceQueryOverride),
  );
  const parameters = new URL(window.location.href).searchParams;
  const fixtureMode = parameters.get('fixture') === '1' && fixtureLoader !== undefined;
  const initialScreenId = parameters.get('screen');
  const [manifest, setManifest] = useState<SurfaceManifest | null>(null);
  const [manifestRevision, setManifestRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(initialScreenId);
  const [screenData, setScreenData] = useState<Readonly<Record<string, JsonValue>>>({});
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [domainRegistry, setDomainRegistry] = useState<UiComponentRegistry>({});
  const [componentLoading, setComponentLoading] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);

  const selectScreen = useCallback((screenId: string) => {
    const location = new URL(window.location.href);
    location.searchParams.set('screen', screenId);
    window.history.pushState({}, '', location);
    setSelectedScreenId(screenId);
    setDataError(null);
    setComponentError(null);
  }, []);

  useEffect(() => {
    const restoreLocation = () => {
      setSurface(requestedSurface(configuredSurface, true));
      setSelectedScreenId(new URL(window.location.href).searchParams.get('screen'));
      setManifest(null);
      setManifestRevision((revision) => revision + 1);
      setError(null);
    };
    window.addEventListener('popstate', restoreLocation);
    return () => window.removeEventListener('popstate', restoreLocation);
  }, [configuredSurface]);

  useEffect(() => {
    document.documentElement.dataset['surface'] = surface;
    if (fixtureMode) {
      document.documentElement.dataset['designSystem'] =
        surface === 'ops' ? 'ops-neutral' : 'arcade';
    }
  }, [fixtureMode, surface]);

  useEffect(() => {
    let active = true;
    if (fixtureMode && fixtureLoader) {
      void fixtureLoader(surface)
        .then((value) => {
          if (!active) return;
          document.documentElement.dataset['designSystem'] = value.designSystemId;
          setManifest(value);
          setError(null);
        })
        .catch((reason: unknown) => {
          if (active) setError(reason instanceof Error ? reason.message : String(reason));
        });
      return () => {
        active = false;
      };
    }

    const controller = new AbortController();
    void fetch(apiRequestUrl(apiBaseUrl, manifestPath(surface)), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`manifest request failed (${response.status})`);
        }
        return (await response.json()) as SurfaceManifest;
      })
      .then((value) => {
        document.documentElement.dataset['designSystem'] = value.designSystemId;
        setManifest(value);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => controller.abort();
  }, [apiBaseUrl, fixtureLoader, fixtureMode, manifestRevision, surface]);

  const execute = useCallback(
    async (operationId: string, input: JsonValue): Promise<unknown> => {
      if (!manifest) throw new Error('體驗尚未載入');
      if (fixtureMode) return { fixture: true };
      const route = manifest.operationRoutes[operationId];
      if (!route) throw new Error(`未註冊操作：${operationId}`);
      const contextualInput: JsonValue =
        typeof input === 'object' && input !== null && !Array.isArray(input)
          ? { ...input, venueId: manifest.venueId }
          : input;
      const request = operationRequest(route, contextualInput);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'UniversalBookingShell',
        ...request.headers,
      };
      if (route.idempotency !== 'not-applicable') {
        headers['Idempotency-Key'] = crypto.randomUUID();
      }
      if (surface === 'ops' && route.mutability === 'write' && !localBearerMode) {
        const csrfResponse = await fetch(apiRequestUrl(apiBaseUrl, '/api/auth/csrf'), {
          credentials: 'include',
        });
        if (!csrfResponse.ok) throw new Error('無法取得安全操作憑證');
        const csrfBody = (await csrfResponse.json()) as { token?: unknown };
        if (typeof csrfBody.token !== 'string') {
          throw new Error('安全操作憑證格式錯誤');
        }
        headers['X-CSRF-Token'] = csrfBody.token;
      }
      const response = await fetch(apiRequestUrl(apiBaseUrl, request.path), {
        method: route.method,
        credentials: 'include',
        headers,
        ...(request.body === undefined ? {} : { body: request.body }),
      });
      if (!response.ok) {
        throw new Error(`操作暫時無法使用（${response.status}）`);
      }
      return JsonValueSchema.parse(await response.json());
    },
    [apiBaseUrl, fixtureMode, localBearerMode, manifest, surface],
  );
  const navigate = useCallback((target: InternalNavigationTarget) => {
    const location = new URL(window.location.href);
    location.searchParams.set('surface', target.surface);
    location.searchParams.set('screen', target.screenId);
    window.history.pushState({}, '', location);
    setManifest(null);
    setManifestRevision((revision) => revision + 1);
    setError(null);
    setDataError(null);
    setComponentError(null);
    setSelectedScreenId(target.screenId);
    setSurface(target.surface);
  }, []);

  const screenDocument = useMemo(() => {
    if (!manifest) return undefined;
    return (
      manifest.screens.find(({ screenId }) => screenId === selectedScreenId) ??
      defaultScreen(manifest.screens)
    );
  }, [manifest, selectedScreenId]);
  const componentIds = useMemo(
    () =>
      screenDocument
        ? Object.values(screenDocument.nodes).flatMap((node) =>
            node.type === 'DomainComponent' && node.componentId ? [node.componentId] : [],
          )
        : [],
    [screenDocument],
  );

  useEffect(() => {
    let active = true;
    setComponentLoading(componentIds.length > 0);
    setComponentError(null);
    void loadUiComponentRegistry(componentIds, generatedUiComponentLoaders)
      .then((registry) => {
        if (!active) return;
        setDomainRegistry(registry);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setDomainRegistry({});
        setComponentError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (active) setComponentLoading(false);
      });
    return () => {
      active = false;
    };
  }, [componentIds]);

  useEffect(() => {
    if (!manifest || !screenDocument) return;
    if (selectedScreenId !== screenDocument.screenId) {
      setSelectedScreenId(screenDocument.screenId);
    }
    setScreenData(manifest.data);
    setDataError(null);
    if (fixtureMode || screenDocument.dataSources.length === 0) return;
    let active = true;
    setDataLoading(true);
    void Promise.all(
      screenDocument.dataSources.map(async (source) => {
        const sourceInput = source.input ? evaluateBinding(source.input, manifest.data) : {};
        const result = JsonValueSchema.parse(await execute(source.operationId, sourceInput));
        return { bindAs: source.bindAs, result };
      }),
    )
      .then((results) => {
        if (!active) return;
        setScreenData(
          results.reduce(
            (data, result) => bindData(data, result.bindAs, result.result),
            manifest.data,
          ),
        );
        setDataError(null);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setDataError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (active) setDataLoading(false);
      });
    return () => {
      active = false;
    };
  }, [execute, fixtureMode, manifest, screenDocument, selectedScreenId]);

  if (error) {
    return (
      <main className="mdr-page" role="alert">
        <p className="mdr-eyebrow">體驗設定</p>
        <h1>目前無法載入這個體驗</h1>
        <p>系統已停止套用不完整設定。請提供畫面上的識別資訊給管理員，或稍後再試。</p>
        <code>{error}</code>
      </main>
    );
  }
  if (!manifest) {
    return (
      <main className="mdr-page" aria-busy="true">
        <p className="mdr-eyebrow">體驗設定</p>
        <h1>正在組裝體驗…</h1>
      </main>
    );
  }
  if (!screenDocument) {
    return (
      <main className="mdr-page">
        <h1>{manifest.copy['state.empty'] ?? '目前沒有可展示內容。'}</h1>
      </main>
    );
  }

  const experienceContent = (
    <>
      {dataLoading ? (
        <p className="mdr-data-status" role="status">
          {manifest.copy[screenDocument.states.loadingCopyKey] ?? '正在載入…'}
        </p>
      ) : null}
      {dataError ? (
        <p className="mdr-data-status mdr-data-status--error" role="alert">
          {manifest.copy[screenDocument.states.errorCopyKey] ?? '資料載入失敗'}：{dataError}
        </p>
      ) : null}
      {componentError ? (
        <p className="mdr-data-status mdr-data-status--error" role="alert">
          專用功能元件載入失敗：{componentError}
        </p>
      ) : null}
      {componentLoading ? (
        <p className="mdr-data-status" role="status">
          正在載入專用功能…
        </p>
      ) : null}
      {!dataLoading && !componentLoading && !componentError ? (
        <MdrRenderer
          document={screenDocument}
          data={screenData}
          copy={manifest.copy}
          domainRegistry={domainRegistry}
          execute={execute}
          navigate={navigate}
        />
      ) : null}
    </>
  );

  if (surface === 'ops') {
    return (
      <OpsProductFrame
        screens={manifest.screens}
        selectedScreenId={screenDocument.screenId}
        copy={manifest.copy}
        onSelectScreen={selectScreen}
      >
        {experienceContent}
      </OpsProductFrame>
    );
  }

  return (
    <>
      {manifest.screens.length > 1 ? (
        <nav className="mdr-screen-nav" aria-label="功能畫面導覽">
          <label>
            <span>功能畫面</span>
            <select
              aria-label="功能畫面"
              value={screenDocument.screenId}
              onChange={(event) => selectScreen(event.currentTarget.value)}
            >
              {manifest.screens.map((screen) => (
                <option key={screen.screenId} value={screen.screenId}>
                  {manifest.copy[screen.titleCopyKey] ?? screen.screenId}
                </option>
              ))}
            </select>
          </label>
        </nav>
      ) : null}
      {experienceContent}
    </>
  );
}

export { operationRequest, type SurfaceOperationRoute } from './operation-request.js';
